import { Box, Button } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTus } from "use-tus";

import Loading from "@/components/Loading/Index.tsx";
import { FileWithMetaType, removeAllItemsFromStorage, toString } from "@/hooks/UseTusHooks/Generics.tsx";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { DataContext } from "@/models/DataProvider.tsx";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { fetchUploadConfig } from "@/utils";
import TusHooksDropZone from "./TusHooksDropZone";

export default function TusHooks({
  setUploadContent,
  feedId,
  autoClick,
}: {
  setUploadContent?: ({ itemId, contentId, name }: { contentId: string; itemId: string; name?: string }) => void;
  feedId?: string;
  autoClick?: boolean;
}) {
  const { config } = useContext(AppContext);
  const { tusUrl, stage } = fetchUploadConfig(config);
  const { upload, setUpload, isSuccess, error, isUploading, remove } = useTus();
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { preferredLanguage } = useContext(DataContext);
  const { myAccountId } = useContext(MyAccountContext);
  const [contentId, setContentId] = useState<string | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    removeAllItemsFromStorage("tus::tus");
  }, []);

  const resetValues = () => {
    setUpload(null);
    remove();
    setUploadError(null);
    setUploadSuccess(false);
  };

  const handleFileUpload = (data: FileWithMetaType | null) => {
    if (!data) {
      return;
    }
    const stringValues = toString(data.meta);
    const metadata = {
      ...stringValues,
      feedItemId: data.meta.itemId,
      feedId: feedId ?? "",
      accountId: myAccountId,
      myAccount: myAccountId,
      stage,
      preferredLanguage,
      startTime: new Date().toISOString(),
      isSilent: true,
      filename: data.meta.name,
      filetype: data.meta.filetype,
    };
    setContentId(data?.meta?.contentId);
    setItemId(data?.meta?.itemId);
    setFileName(data?.meta?.filename ?? null);
    const tusPayload = {
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      endpoint: tusUrl,
      metadata,
    };
    setUpload(data.file, tusPayload);
  };

  const startUpload = useRef<boolean>(false);

  useEffect(() => {
    if (upload !== undefined && !startUpload.current) {
      startUpload.current = true;
      upload?.start();
    }

    if (error) {
      startUpload.current = false;
      setUpload(null);
      remove();
      setUploadError(error?.message || "There was an error uploading");
    }

    if (isSuccess && contentId && itemId) {
      setUploadSuccess(true);
      startUpload.current = false;
      setUploadError(null);
      remove();
      if (setUploadContent) {
        setUploadContent({ contentId, itemId, name: fileName ?? undefined });
      }
    }
  }, [isSuccess, error, upload, contentId, itemId, fileName, setUploadContent]);

  const borderColor = uploadSuccess ? "green" : uploadError ? "red" : "grey";

  return (
    <Box sx={{ width: "100%" }}>
      {!uploadError && !isUploading && !uploadSuccess ? (
        <TusHooksDropZone
          sx={{
            position: "relative",
            maxHeight: "390px",
            overflowY: "auto",
          }}
          autoClick={autoClick}
          setUploadData={handleFileUpload}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            border: `dashed 2px ${borderColor}`,
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            borderRadius: "1rem",
            gap: 2,
            minHeight: "120px",
          }}
        >
          {uploadSuccess && contentId && <Box>File Uploaded</Box>}
          {isUploading && !uploadError && !uploadSuccess && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box>Uploading</Box>
              <Loading variant="white" size="large" baseSize={6} />
            </Box>
          )}
          {uploadError && !uploadSuccess && (
            <Box>
              <Box sx={{ mb: 1 }}>There was an error uploading</Box>
              <Button onClick={() => resetValues()}>Try Again</Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
